import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ThumbsUp, ThumbsDown, ChatCircle, BookmarkSimple,
    MapPin, EnvelopeSimple, Star, LockKey, Rocket, Trash,
    DotsThreeVertical, Share, Flag, PaperPlaneTilt, Lock,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { RichText } from "./RichText";
import PostImageCarousel from "./PostImageCarousel";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { ProRequiredModal } from "../pages/Messages";

// `"pro"` is the legacy single-tier value still on a chunk of users in
// the DB — the backend's subscription_tier_of() maps it to pro_global,
// so we must too or those users incorrectly see the upgrade prompt.
const PRO_TIERS = new Set(["pro", "pro_local", "pro_global"]);
function isPro(u) {
    return !!u && (u.role === "admin" || PRO_TIERS.has(u.subscription));
}

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
}

function priceLabel(p) {
    if (!p?.price) return "";
    const sym = p.currency_symbol || (p.currency ? p.currency.toUpperCase() + " " : "$");
    const amt = p.price >= 100 ? Math.round(p.price).toLocaleString() : p.price.toFixed(2);
    return `${sym}${amt}`;
}

function daysLeft(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return null;
    return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function PostCard({ post, onUpdate, onDelete, detail = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [p, setP] = useState(post);
    const [busy, setBusy] = useState(false);
    const [boosting, setBoosting] = useState(false);
    const [boostLabel, setBoostLabel] = useState(null);
    const [deleted, setDeleted] = useState(false);
    const [proModal, setProModal] = useState(false);

    const isAuthor = user?.id === p.author_id;
    const boostActive = !!p.featured && (!p.featured_until || daysLeft(p.featured_until));

    // Pre-fetch the regional Boost price so the button reads "Boost · ₹250"
    useEffect(() => {
        if (!isAuthor || boostActive) return;
        api.get("/payments/unlock-price")
            .then((r) => setBoostLabel(r.data?.plans?.boost_post?.label || null))
            .catch(() => {});
    }, [isAuthor, boostActive]);

    const requireAuth = () => {
        if (!user) {
            toast.message("Sign in to continue", {
                description: "Like, save and comment — free Ideas Mine account.",
                action: { label: "Sign in", onClick: () => navigate(`/auth?next=${encodeURIComponent(window.location.pathname)}`) },
            });
            return false;
        }
        return true;
    };

    // Paywall guard for like/dislike/save on locked admin-curated posts.
    // Backend enforces 402; this is UX so the user sees the upgrade card
    // instead of a silent failure. Returns true if the click is blocked.
    const blockedByPaywall = () => {
        if (!p.is_locked || isAuthor || user?.role === "admin") return false;
        toast.message("Upgrade to Pro to interact with this idea", {
            description: "Like, save and comment on Startup Guides — Pro members only.",
            action: { label: "View", onClick: () => navigate(`/post/${p.id}`) },
        });
        return true;
    };

    const handleLike = async () => {
        if (!requireAuth() || busy) return;
        if (blockedByPaywall()) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/posts/${p.id}/like`);
            const next = { ...p, ...data };
            setP(next);
            onUpdate && onUpdate(next);
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.message("Upgrade to Pro to like this idea", {
                    action: { label: "View", onClick: () => navigate(`/post/${p.id}`) },
                });
            } else { toast.error("Failed"); }
        }
        finally { setBusy(false); }
    };

    const handleDislike = async () => {
        if (!requireAuth() || busy) return;
        if (blockedByPaywall()) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/posts/${p.id}/dislike`);
            const next = { ...p, ...data };
            setP(next);
            onUpdate && onUpdate(next);
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.message("Upgrade to Pro to react to this idea", {
                    action: { label: "View", onClick: () => navigate(`/post/${p.id}`) },
                });
            } else { toast.error("Failed"); }
        }
        finally { setBusy(false); }
    };

    const handleSave = async () => {
        if (!requireAuth()) return;
        if (blockedByPaywall()) return;
        try {
            const { data } = await api.post(`/posts/${p.id}/save`);
            setP({ ...p, saved: data.saved });
            toast.success(data.saved ? "Saved" : "Removed");
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.message("Upgrade to Pro to save this idea", {
                    action: { label: "View", onClick: () => navigate(`/post/${p.id}`) },
                });
            } else { toast.error("Failed"); }
        }
    };

    const handleReport = async () => {
        if (!requireAuth()) return;
        try {
            await api.post(`/posts/${p.id}/report`);
            toast.success("Reported. Admins will review.");
        } catch { toast.error("Failed"); }
    };

    const handleMessageAuthor = async () => {
        if (!requireAuth()) return;
        if (isAuthor) return;
        if (!isPro(user)) { setProModal(true); return; }
        try {
            const { data } = await api.post("/messages", {
                recipient_id: p.author_id,
                body: `Hi! I saw your post "${p.title.slice(0, 60)}" — wanted to reach out.`,
            });
            toast.success("Message sent");
            navigate(`/messages/${data.conversation_id}`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start conversation";
            toast.error(typeof msg === "string" ? msg : "Could not start conversation");
        }
    };

    const handleShare = async () => {
        const url = `${window.location.origin}/post/${p.id}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied");
        } catch { toast.error("Couldn't copy"); }
    };

    const handleBoost = async () => {
        if (!requireAuth() || boosting) return;
        setBoosting(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan: "boost_post",
                post_id: p.id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start checkout";
            toast.error(typeof msg === "string" ? msg : "Could not start checkout");
            setBoosting(false);
        }
    };

    const handleDelete = async () => {
        if (!requireAuth()) return;
        if (!window.confirm("Delete this idea permanently? This can't be undone.")) return;
        try {
            await api.delete(`/posts/${p.id}`);
            toast.success("Idea deleted");
            setDeleted(true);
            onDelete && onDelete(p.id);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not delete";
            toast.error(typeof msg === "string" ? msg : "Could not delete");
        }
    };

    const forSale = p.is_paid && p.author_role === "user" && p.price;
    // Backend now stamps `contact_visible` on paid user-posts.
    const contactUnlocked = !forSale || isAuthor || p.contact_visible;
    const boostDays = p.featured_until ? daysLeft(p.featured_until) : null;
    const canDelete = isAuthor || user?.role === "admin";

    // Paid boosts (featured_until is set) should only be visible to the
    // author and admins. Admin-curated features (no featured_until) stay
    // visible to everyone so editorial picks still read as such.
    const isPaidBoost = !!p.featured_until;
    const canSeeBoostBadge = !isPaidBoost || isAuthor || user?.role === "admin";

    if (deleted) return null;

    return (
        <article
            data-testid={`post-card-${p.id}`}
            className="idea-card relative rounded-2xl border border-border bg-card overflow-hidden"
        >
            {p.featured && canSeeBoostBadge && (
                <div className="absolute top-3 right-3 z-10" data-testid={`featured-badge-${p.id}`}>
                    <Badge className="bg-[#FF8A00] hover:bg-[#FF8A00] text-white border-0">
                        <Star size={12} weight="fill" className="mr-1" />
                        {isPaidBoost
                            ? `Boosted${boostDays ? ` · ${boostDays}d left` : ""}`
                            : "Featured"}
                    </Badge>
                </div>
            )}
            <div className="p-6 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#FF8A00] grid place-items-center text-white font-bold">
                        {(p.author_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        {!isAuthor && p.author_id ? (
                            <button
                                type="button"
                                onClick={handleMessageAuthor}
                                className="text-sm font-semibold truncate hover:text-[#6C63FF] hover:underline underline-offset-2 transition-colors text-left"
                                data-testid={`postcard-author-${p.id}`}
                                aria-label={`Message ${p.author_name}`}
                            >
                                {p.author_name}
                            </button>
                        ) : (
                            <div className="text-sm font-semibold truncate">{p.author_name}</div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <MapPin size={12} weight="duotone" />
                            <span>{p.country}</span>
                            <span>·</span>
                            <span>{timeAgo(p.created_at)} ago</span>
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                            {p.category}
                        </Badge>
                        {forSale ? (
                            <Badge className="bg-[#FF8A00]/10 text-[#FF8A00] border-[#FF8A00]/30 hover:bg-[#FF8A00]/10">
                                For sale · {priceLabel(p)}
                            </Badge>
                        ) : p.is_paid ? (
                            <Badge className="bg-[#FF8A00]/10 text-[#FF8A00] border-[#FF8A00]/30 hover:bg-[#FF8A00]/10">
                                Paid
                            </Badge>
                        ) : (
                            <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/10">
                                Free
                            </Badge>
                        )}
                    </div>
                </div>

                <Link to={`/post/${p.id}`} data-testid={`post-title-link-${p.id}`}>
                    <h3 className="font-display font-black text-xl md:text-2xl leading-tight tracking-tight mb-2 hover:text-[#6C63FF] transition-colors">
                        {p.title}
                    </h3>
                </Link>
                {/* Description: on the detail page we render the full,
                    non-clamped body; on feed cards we clip to 3 lines and
                    expose a "Read more →" affordance that navigates to the
                    detail view. */}
                {detail ? (
                    <div
                        data-testid={`post-desc-${p.id}`}
                        className="text-sm md:text-base text-foreground/90 leading-relaxed whitespace-pre-wrap break-words"
                    >
                        <RichText text={p.description} />
                    </div>
                ) : (
                    <Link to={`/post/${p.id}`} data-testid={`post-desc-link-${p.id}`} className="block group">
                        <p className="text-sm md:text-base text-muted-foreground line-clamp-3 leading-relaxed group-hover:text-foreground/80 transition-colors">
                            <RichText text={p.description} />
                        </p>
                        {(p.description || "").length > 180 && (
                            <span className="inline-block mt-2 text-xs font-semibold text-[#6C63FF] group-hover:underline">
                                Read more →
                            </span>
                        )}
                    </Link>
                )}

                {(p.image_urls?.length || p.image_url) && (() => {
                    // Prefer new multi-image array; fall back to legacy singular.
                    const imgs = (p.image_urls && p.image_urls.length ? p.image_urls : (p.image_url ? [p.image_url] : []));
                    return detail ? (
                        <div className="mt-4">
                            <PostImageCarousel
                                images={imgs}
                                alt={p.title}
                                maxHeightClass="h-auto"
                                testId={`post-image-${p.id}`}
                            />
                        </div>
                    ) : (
                        <Link to={`/post/${p.id}`} className="block mt-4" onClick={(e) => {
                            // If click landed on a carousel control (arrow/dot),
                            // let the carousel handle it — its handlers already
                            // call preventDefault, so this branch is defensive.
                            if (e.defaultPrevented) return;
                        }}>
                            <PostImageCarousel
                                images={imgs}
                                alt={p.title}
                                maxHeightClass="max-h-[480px]"
                                testId={`post-image-preview-${p.id}`}
                            />
                        </Link>
                    );
                })()}

                {forSale && !contactUnlocked && (
                    <div
                        className="flex flex-wrap items-center justify-between gap-3 mb-4 rounded-xl border border-[#6C63FF]/30 bg-[#6C63FF]/5 p-3"
                        data-testid={`gated-contact-${p.id}`}
                    >
                        <div className="flex items-center gap-2 text-sm">
                            <LockKey size={18} weight="duotone" className="text-[#6C63FF]" />
                            <div>
                                <div className="font-semibold">Seller&apos;s contact is Pro-only</div>
                                <div className="text-xs text-muted-foreground">
                                    Upgrade to reach the founder behind this idea.
                                </div>
                            </div>
                        </div>
                        <Link
                            to="/subscription"
                            data-testid={`upgrade-cta-${p.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#6C63FF] hover:bg-[#6C63FF]/90 transition-colors"
                        >
                            Upgrade to Pro
                        </Link>
                    </div>
                )}

                {isAuthor && !boostActive && (
                    <div
                        className="flex flex-wrap items-center justify-between gap-3 mb-4 rounded-xl border border-[#FF8A00]/30 bg-[#FF8A00]/5 p-3"
                        data-testid={`boost-cta-${p.id}`}
                    >
                        <div className="flex items-center gap-2 text-sm">
                            <Rocket size={18} weight="duotone" className="text-[#FF8A00]" />
                            <div>
                                <div className="font-semibold">Boost this idea</div>
                                <div className="text-xs text-muted-foreground">
                                    Pin it to the top of the feed for 7 days · one-time {boostLabel || "fee"}.
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleBoost}
                            disabled={boosting}
                            data-testid={`boost-btn-${p.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#FF8A00] hover:bg-[#FF8A00]/90 disabled:opacity-60 transition-colors"
                        >
                            <Rocket size={14} weight="fill" />
                            {boosting ? "Redirecting…" : `Boost${boostLabel ? ` · ${boostLabel}` : ""}`}
                        </button>
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-1 -ml-2">
                    <button
                        onClick={handleLike}
                        data-testid={`like-${p.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                            ${p.liked ? "bg-[#6C63FF]/10 text-[#6C63FF]" : "hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"}`}
                    >
                        <ThumbsUp size={16} weight={p.liked ? "fill" : "regular"} />
                        <span>{p.likes_count || 0}</span>
                    </button>
                    <button
                        onClick={handleDislike}
                        data-testid={`dislike-${p.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                            ${p.disliked ? "bg-red-500/10 text-red-500" : "hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"}`}
                    >
                        <ThumbsDown size={16} weight={p.disliked ? "fill" : "regular"} />
                        <span>{p.dislikes_count || 0}</span>
                    </button>
                    <Link
                        to={`/post/${p.id}`}
                        data-testid={`comment-${p.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    >
                        <ChatCircle size={16} weight="duotone" />
                        <span>{p.comments_count || 0}</span>
                    </Link>
                    <button
                        onClick={handleSave}
                        data-testid={`save-${p.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                            ${p.saved ? "bg-[#FF8A00]/10 text-[#FF8A00]" : "hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"}`}
                    >
                        <BookmarkSimple size={16} weight={p.saved ? "fill" : "regular"} />
                    </button>

                    {/* 3-dot menu: Message author / Share post / Report post / Delete */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid={`post-menu-${p.id}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors ml-auto"
                                title="More actions"
                            >
                                <DotsThreeVertical size={18} weight="bold" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            {!isAuthor && (
                                <DropdownMenuItem
                                    onClick={handleMessageAuthor}
                                    data-testid={`post-menu-message-${p.id}`}
                                >
                                    {isPro(user)
                                        ? <><PaperPlaneTilt size={14} className="mr-2" />Message author</>
                                        : <><Lock size={14} className="mr-2" />Message author (Pro)</>}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={handleShare}
                                data-testid={`post-menu-share-${p.id}`}
                            >
                                <Share size={14} className="mr-2" />Share post
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={handleReport}
                                data-testid={`post-menu-report-${p.id}`}
                            >
                                <Flag size={14} className="mr-2" />Report post
                            </DropdownMenuItem>
                            {canDelete && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={handleDelete}
                                        data-testid={`post-menu-delete-${p.id}`}
                                        className="text-red-500 focus:text-red-500"
                                    >
                                        <Trash size={14} className="mr-2" />Delete idea
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {p.contact_email && contactUnlocked && (
                        <a
                            href={`mailto:${p.contact_email}`}
                            data-testid={`contact-${p.id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#6C63FF] hover:bg-[#6C63FF]/10 transition-colors"
                        >
                            <EnvelopeSimple size={16} weight="duotone" />
                            <span className="hidden sm:inline">Contact seller</span>
                        </a>
                    )}
                </div>
            </div>
            <ProRequiredModal
                open={proModal}
                onOpenChange={setProModal}
                action="message this user"
            />
        </article>
    );
}
